import React, { useCallback, useMemo, useState } from "react";
import Loadable from "@react-loadable/revised";
import { useLocation } from "react-router-dom";
import VisiblitySensor from "react-visibility-sensor";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { TreeView } from "@components_v2/tree-view";
import FolderTreeViewSkeleton from "@components/tree/components/folder-tree-view-skeleton";
import FolderTreeViewItem from "@components/tree/components/folder-tree-view-item";
import ReactDOM from "react-dom";
import { archiveAssetAPI } from "@modules/apis/assets";
import DialogArchive from "@features/dialog/dialog-archive";
import DialogArchiveRestore from "@features/dialog/dialog-archive-restore";
import DialogAssetDuplicate from "@routes/asset/dialog-asset-folder-duplicate";
import {
    deleteFolder,
    getFoldersList,
    initFolderData,
    moveFolder,
    updateFolderInfo,
    updateMovedFolder,
} from "@modules/folderTreeView";
import { DRIVE_FOLDER_SEARCH_TYPE } from "@constants";
import { setNotification } from "@modules/notification";
import { siteMode } from "@cores/siteMode";
import menuService from "@services/menuService";
import { listJobs } from "@modules/bulkJobs";
import useCheckPermission from "@hooks/useCheckPermission";
import useArchive from "@hooks/useArchive";
import DialogCreateCollection from "@routes/collections/dialog-create";
import compareVersions, {
    VERSION_CM_11210_ASSET_DUPLICATION,
    VERSION_CM_12200_CREATE_COLLECTION_WITH_ASSET_FOLDER,
    VERSION_CM_12290_COLLECTION_ARCHIVE,
} from "@cores/version";
import useDownloadJobs from "@hooks/useDownloadJobs";
import useFolder from "@hooks/useFolder";

const LoadableCreateFolderModal = Loadable({
    loader: () => import("../modals/NewFolderModal"),
    loading: () => null,
});

const LoadableDeleteFolderModal = Loadable({
    loader: () => import("../../routes/asset/ui/DeleteFolderConfirm"),
    loading: () => null,
});

const LoadableSelectFolderPathModal = Loadable({
    loader: () => import("../modals/SelectFolderPath"),
    loading: () => null,
});

const LoadableFolderRenameModal = Loadable({
    loader: () => import("../modals/FolderRenameModal"),
    loading: () => null,
});
const LoadableDownloadLinkModal = Loadable({
    loader: () => import("@components/modals/DownloadLinkModal"),
    loading: () => null,
});

const FolderTreeView = ({
                            open,
                            domain,
                            driveInfo,
                            resourceType,
                            isModal,
                            driveList,
                            folderList,
                            onHandleCreateFolder,
                            onClickForderTree,
                            isSearching,
                            selectedFolder,
                            onRefresh,
                            useActionMenus = false,
                            isCustomMenu,
                            isCms,
                        }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();
    const { spaceId, projectId } = menuService.parse(location.pathname);

    const FOLDERS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Folders" });
    const ASSETS_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Assets" });
    const SHARES_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Shares" });
    const DOWNLOAD_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Download" });

    const { requestArchive } = useArchive();
    const { requestDownloadJob } = useDownloadJobs();
    const { onRenameFolder } = useFolder({});

    const { project, folderTreeViewModal, stage } = useSelector((store) => ({
        project: store.project,
        folderTreeViewModal: store.folderTreeViewModal,
        stage: store.stage,
    }));

    const [dialog, setDialog] = useState("");
    const [selectedFolderByMenu, setSelectedFolderByMenu] = useState(null);
    const [createdFolderId, setCreatedFolderId] = useState("");
    const [actionPending, setActionPending] = useState({});

    const onClickResetFolderTreeFilter = async (driveId, rootFolder) => {
        onClickForderTree(rootFolder);
    };

    const onHandleClickCloseDialog = () => {
        setDialog("");
        setSelectedFolderByMenu(null);
    };

    const drive = useMemo(() => {
        if (!driveList.data) return {};
        return driveList.data?.find((el) => el.resourceType?.type === domain.toUpperCase());
    }, [driveList, domain]);

    const onHandleClickDownloadFolder = useCallback((domain, folder) => {
        const downloadRequestTargets = {
            virtualFolder: { id: folder.id, recursive: false },
            projectId: projectId,
            resourceName: folder.name,
        };
        requestDownloadJob(domain.toUpperCase(), downloadRequestTargets, 1, false);
    }, [requestDownloadJob]);

    const getTreeActionsMenu = useCallback(
        (folderObj) => {
            if (isModal || siteMode.get() === "CMS" || !useActionMenus) return undefined;

            switch (resourceType) {
                case "Asset":
                    return [
                        {
                            label: t(`common::label::New Folder`),
                            id: "create",
                            clickClose: true,
                            onClick: () => {
                                setDialog("create");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Move`),
                            id: "move",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                setDialog("move");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Rename`),
                            id: "rename",
                            clickClose: true,
                            onClick: () => {
                                setDialog("rename");
                                setSelectedFolderByMenu({
                                    ...folderObj,
                                    driveInfo: drive,
                                    parentInfo: folderObj.parent ? folderList.data?.[folderObj.parent] : {},
                                });
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Duplicate`),
                            id: "duplicate",
                            clickClose: true,
                            hidden: compareVersions(stage.version, VERSION_CM_11210_ASSET_DUPLICATION) < 0,
                            onClick: () => {
                                setDialog("duplicate");
                                setSelectedFolderByMenu({
                                    ...folderObj,
                                    driveInfo: drive,
                                    parentInfo: folderObj.parent ? folderList.data?.[folderObj.parent] : {},
                                });
                            },
                            disabled: !ASSETS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Delete`),
                            id: "delete",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                setDialog("delete");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Create Collection`),
                            id: "createCollection",
                            clickClose: true,
                            divider: true,
                            hidden:
                                compareVersions(stage.version, VERSION_CM_12200_CREATE_COLLECTION_WITH_ASSET_FOLDER) <
                                0,
                            onClick: () => {
                                setDialog("createCollection");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !ASSETS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Archive`),
                            id: "archive",
                            clickClose: true,
                            divider: true,
                            disabled: !ASSETS_PERMISSIONS.ARCHIVE,
                            onClick: () => {
                                setDialog("archive");
                                setSelectedFolderByMenu(folderObj);
                            },
                        },
                    ];
                case "Collection":
                    return [
                        {
                            label: t(`common::label::New Folder`),
                            id: "create",
                            clickClose: true,
                            onClick: () => {
                                setDialog("create");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Share link`),
                            id: "move",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                setDialog("shareLink");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !SHARES_PERMISSIONS.EXTERNAL,
                        },
                        {
                            label: t(`common::label::Move`),
                            id: "move",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                setDialog("move");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !ASSETS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Rename`),
                            id: "rename",
                            clickClose: true,
                            onClick: () => {
                                setDialog("rename");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Download`),
                            id: "download",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                onHandleClickDownloadFolder(domain, folderObj);
                            },
                            disabled: !DOWNLOAD_PERMISSIONS.DOWNLOAD,
                        },
                        {
                            label: t(`common::label::Delete`),
                            id: "delete",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                setDialog("delete");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Archive`),
                            id: "archive",
                            clickClose: true,
                            divider: true,
                            hidden: compareVersions(stage.version, VERSION_CM_12290_COLLECTION_ARCHIVE) < 0,
                            onClick: () => {
                                setDialog("archive");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !ASSETS_PERMISSIONS.ARCHIVE,
                        },
                    ];
                case "Archived":
                    return [
                        {
                            label: t(`common::label::Restore`),
                            id: "restore",
                            clickClose: true,
                            onClick: () => {
                                setDialog("restore");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !ASSETS_PERMISSIONS.ARCHIVE,
                        },
                    ];
                case "CustomContent":
                    return [
                        {
                            label: t(`common::label::New Folder`),
                            id: "create",
                            clickClose: true,
                            onClick: () => {
                                setDialog("create");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Move`),
                            id: "move",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                setDialog("move");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Rename`),
                            id: "rename",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                setDialog("rename");
                                setSelectedFolderByMenu({
                                    ...folderObj,
                                    driveInfo: drive,
                                    parentInfo: folderObj.parent ? folderList.data?.[folderObj.parent] : {},
                                });
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                        {
                            label: t(`common::label::Delete`),
                            id: "delete",
                            clickClose: true,
                            divider: true,
                            onClick: () => {
                                setDialog("delete");
                                setSelectedFolderByMenu(folderObj);
                            },
                            disabled: !FOLDERS_PERMISSIONS.EDIT,
                        },
                    ];
            }
        },
        [
            resourceType,
            driveInfo,
            stage,
            isModal,
            driveList,
            folderList,
            drive,
            ASSETS_PERMISSIONS,
            FOLDERS_PERMISSIONS,
            FOLDERS_PERMISSIONS,
            DOWNLOAD_PERMISSIONS,
            requestDownloadJob,
        ],
    );

    const onHandleClickFolder = useCallback(
        async (folder, isClickArrow) => {
            if (actionPending?.[folder.id]) return;

            setActionPending({ ...actionPending, [folder.id]: true });
            try {
                await onClickForderTree(folder, isClickArrow);
            } finally {
                setActionPending({ ...actionPending, [folder.id]: false });
            }
        },
        [actionPending],
    );

    const renderTree = useCallback(
        (parentFolderId, children = []) => {
            if (folderList.pending || children.length === 0) return;
            return (
                <React.Fragment key={`folder-tree-view-item-${parentFolderId}-children-container`}>
                    {children.map((folder, index) => {
                        const folderObj = folderList.data[folder];
                        if (!folderObj) return null;
                        return (
                            <React.Fragment key={`folder-tree-view-item-${index}`}>
                                <VisiblitySensor
                                    active={
                                        !isSearching &&
                                        index === children.length - 1 &&
                                        !!folderList.data?.[folderObj.parent]?.nextToken
                                    }
                                    onChange={(isVisible, sensorElement) => {
                                        if (isVisible) {
                                            onHandleGetChildFolder(folderObj.parent);
                                        }
                                    }}
                                >
                                    <FolderTreeViewItem
                                        folder={folderObj}
                                        onClickFolder={onHandleClickFolder}
                                        onClickCreateFolder={onHandleCreateFolder}
                                        menuList={getTreeActionsMenu(folderObj)}
                                        creatingParentFolder={
                                            isModal ? folderTreeViewModal.folderList?.creatingParentFolder : undefined
                                        }
                                        onToggleFolderFold={(folderId) => onHandleClickFolder(folderId, true)}
                                        renderTree={renderTree}
                                    />
                                </VisiblitySensor>
                            </React.Fragment>
                        );
                    })}
                </React.Fragment>
            );
        },
        [createdFolderId, folderList, folderTreeViewModal, onHandleCreateFolder, onHandleClickFolder],
    );

    const onChangeFolderPath = useCallback(async () => {
        const toFolder = folderTreeViewModal.folderList.selectedFolder.folder;
        const fromObj = { id: selectedFolderByMenu?.id, parent: { id: selectedFolderByMenu?.parent } };
        const toObj = { id: toFolder.id, parent: { id: toFolder.parent } };
        const params = { from: fromObj, to: toObj };
        const driveId = { driveId: selectedFolderByMenu?.driveId };

        try {
            if (toFolder.id !== fromObj.id) {
                await dispatch(moveFolder(driveId, params));

                dispatch(
                    setNotification({
                        type: "success",
                        contents: t(`common::msg::${"Move completed from {{item1}} to {{item2}}."}`, {
                            item1: folderList.data[fromObj.parent.id].name,
                            item2: folderTreeViewModal.folderList.data[toObj.id].name,
                        }),
                    }),
                );
            }

            if (!(folderList.selectedFolder.folder.id === fromObj.id && !folderList.data[toFolder.id])) {
                await dispatch(updateMovedFolder({ fromPosition: fromObj, toPosition: toObj }));
            } else {
                //Note: 현재 선택한 폴더를 이동하는데 트리뷰에 아직 정보가 없는 폴더의 경우 루트폴더로 이동
                const rootFolder = folderList?.data?.[drive.rootFolder.id];
                onClickForderTree(rootFolder);
            }

            onHandleClickCloseDialog();
        } catch (error) {
            const errorData = error && error.response && error.response.data;
            const errorMessage = errorData && errorData.message;
            const errorStatus = (error && error.response && error.response.status) || "Unknown";
            const errorContents = `- ${errorMessage}`;

            dispatch(
                setNotification({
                    type: "error",
                    contents:
                        t(`common::msg::${"Error moving from {{item1}} to {{item2}}."}`, {
                            item1: folderList.data[fromObj.parent.id].name,
                            item2: folderTreeViewModal.folderList.data[toObj.id].name,
                        }) + errorContents,
                }),
            );
        }
    }, [folderList, driveList, folderTreeViewModal, drive]);

    const onClickFolderDelete = useCallback(
        async (item) => {
            try {
                const params = { driveId: item.driveId, folderId: item.id };
                await dispatch(deleteFolder(params));

                const queryParameters = {
                    parentId: item.parent,
                    searchType: DRIVE_FOLDER_SEARCH_TYPE.DIRECT_CHILDREN,
                };
                const headerParams = { projectId };
                if (item.id === folderList.selectedFolder.folder.id) {
                    const rootFolder = await dispatch(
                        initFolderData({
                            headerParams,
                            domain,
                            resourceTypeId: driveInfo?.resourceTypeId,
                        }),
                    );
                    await onClickForderTree(rootFolder, false);
                } else {
                    await dispatch(
                        getFoldersList({
                            driveId: item.driveId,
                            queryParameters,
                            headerParams,
                        }),
                    );
                }
                dispatch(
                    setNotification({
                        type: "success",
                        contents: t(`common::msg::Deletion of folder {{item}} has been completed.`, {
                            item: item.name,
                        }),
                    }),
                );
                onHandleClickCloseDialog();
            } catch (error) {
                const errorData = error && error.response && error.response.data;
                const errorMessage = errorData && errorData.message;
                const errorStatus = (error && error.response && error.response.status) || "Unknown";
                const errorContents =
                    `- ` + errorStatus === 400 ? t(`common::msg::This folder contains assets.`) : `${errorMessage}`;
                console.error(error);

                dispatch(
                    setNotification({
                        type: "error",
                        contents:
                            t(`common::msg::There was an error deleting folder {{item}}.`, {
                                item: item.name,
                            }) + errorContents,
                    }),
                );
            }
        },
        [folderList, driveList, projectId, driveInfo, domain],
    );

    const onHandleGetChildFolder = async (parentId) => {
        const parent = folderList.data[parentId];

        await dispatch(
            getFoldersList({
                driveId: parent.driveId,
                queryParameters: {
                    parentId: parent.id,
                    searchType: DRIVE_FOLDER_SEARCH_TYPE.DIRECT_CHILDREN,
                },
                nextToken: parent.nextToken,
                headerParams: { projectId },
            }),
        );
    };

    const onClickArchiveAssetFolder = async (_, option) => {
        try {
            setActionPending({ ...actionPending, archive: true });

            const id = selectedFolderByMenu?.id;
            if (!id) throw new Error();

            const targets =
                domain === "Asset"
                    ? {
                        virtualFolder: {
                            id,
                            recursive: false,
                        },
                    }
                    : { collection: { virtualFolder: { id, recursive: false } } };

            const data = {
                archiveClass: option,
                targets,
            };

            const isMulti = false;

            await requestArchive(isMulti, data, async () => {
                dispatch(listJobs());
                await onRefresh();
            });
        } catch (e) {
        } finally {
            setActionPending({ ...actionPending, archive: false });
            onHandleClickCloseDialog();
        }
    };

    const rootFolderList = useMemo(() => {
        if (!folderList.data) return null;
        return [Object.values(folderList.data).find((folder) => folder?.depth === 0)];
    }, [folderList.data]);

    const selectedVirtualFolderId = useMemo(
        () => selectedFolder ?? new URLSearchParams(location.search).get("virtualFolderId"),
        [location.search, selectedFolder],
    );

    return (
        <>
            {driveList.pending || folderList.pending ? (
                <FolderTreeViewSkeleton type={"root"} />
            ) : Object.keys(folderList.data ?? {})?.length > 0 ? (
                rootFolderList.length > 0 ? (
                    <TreeView
                        sx={{
                            width: `100%`,
                            minWidth: 318,
                            opacity: 1,
                            overflowY: `auto`,
                            transition: `opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 200ms`,
                            "::-webkit-scrollbar": {
                                width: "14px",
                                height: "14px",
                            },
                            "::-webkit-scrollbar-track": {
                                background: "transparent",
                            },
                            "::-webkit-scrollbar-thumb": {
                                backgroundColor: "#dce1e5",
                                borderRadius: "8px",
                                backgroundClip: "padding-box",
                                border: "4px solid transparent",
                            },
                        }}
                        defaultExpanded={[rootFolderList[0].id]}
                        defaultSelected={[rootFolderList[0].id]}
                        expanded={
                            // 설명: 폴더 닫혀있는 상태에서 하위 폴더 생성시 부모 폴더를 열면서 하위폴더를 보여줘야하는데 스토리북에 expansion에 대한 처리가 되어있어 여기서 제어가 불가능. expanded에서 전체를 다 관리
                            folderList.data &&
                            Object.values(folderList.data)
                                .filter((folder) => !folder.isFold)
                                .map((folder) => folder.id)
                        }
                        selected={[selectedVirtualFolderId]}
                    >
                        {rootFolderList?.map((folder, index) => {
                            return (
                                <FolderTreeViewItem
                                    key={`folder-tree-view-item-${folder.id}`}
                                    folder={folder}
                                    onClickFolder={onHandleClickFolder}
                                    onRefreshDrive={!isModal ? onClickResetFolderTreeFilter : undefined}
                                    onClickCreateFolder={onHandleCreateFolder}
                                    menuList={
                                        ["Asset", "Collection", "Content"].includes(domain) &&
                                        !isModal &&
                                        siteMode.get() !== "CMS" &&
                                        useActionMenus
                                            ? [
                                                {
                                                    label: t(`common::label::New Folder`, `New Folder`),
                                                    onClick: () => {
                                                        setDialog("create");
                                                        setSelectedFolderByMenu(folder);
                                                    },
                                                    buttonDisabled: !FOLDERS_PERMISSIONS.EDIT,
                                                    clickClose: true,
                                                },
                                            ]
                                            : undefined
                                    }
                                    creatingParentFolder={
                                        isModal ? folderTreeViewModal.folderList?.creatingParentFolder : undefined
                                    }
                                    onToggleFolderFold={(folder) => onHandleClickFolder(folder, true)}
                                    renderTree={renderTree}
                                />
                            );
                        })}
                    </TreeView>
                ) : (
                    <div>Please manage your assets by adding the new folder.</div>
                )
            ) : null}

            {dialog === "create" &&
                ReactDOM.createPortal(
                    <LoadableCreateFolderModal
                        folder={selectedFolderByMenu}
                        onClickClose={onHandleClickCloseDialog}
                        onClickSubmit={onHandleCreateFolder}
                    />,
                    document.body,
                )}
            {/* 트리뷰에서 폴더 삭제 모달 */}
            {dialog === "delete" &&
                ReactDOM.createPortal(
                    <LoadableDeleteFolderModal
                        folder={selectedFolderByMenu}
                        onClickClose={onHandleClickCloseDialog}
                        onClickDelete={onClickFolderDelete}
                        onDeleteEnd={() => {
                        }}
                    />,
                    document.body,
                )}
            {/* 트리뷰에서 폴더 이동 모달 */}
            {dialog === "move" &&
                ReactDOM.createPortal(
                    <LoadableSelectFolderPathModal
                        open={true}
                        domain={domain}
                        driveInfo={driveInfo}
                        submitText={`Select`}
                        title={`Move`}
                        onSubmit={onChangeFolderPath}
                        onClose={onHandleClickCloseDialog}
                    />,
                    document.body,
                )}

            {/* 트리뷰에서 폴더 이름변경 모달 */}
            {dialog === "rename" &&
                ReactDOM.createPortal(
                    <LoadableFolderRenameModal
                        folder={selectedFolderByMenu}
                        onClickClose={onHandleClickCloseDialog}
                        onClickSubmit={onRenameFolder}
                    />,
                    document.body,
                )}
            {/* 트리뷰에서 폴터 아카이브 모달  */}
            {dialog === "archive" && (
                <DialogArchive
                    active={true}
                    onClose={onHandleClickCloseDialog}
                    onSubmit={onClickArchiveAssetFolder}
                    actionPending={actionPending.archive}
                    folder={selectedFolderByMenu}
                />
            )}
            {/* 트리뷰에서 폴터 아카이브 복원 모달  */}
            {dialog === "restore" && (
                <DialogArchiveRestore
                    isMulti={true}
                    actionPending={actionPending}
                    onSubmitCallback={onRefresh}
                    onClose={onHandleClickCloseDialog}
                    folders={selectedFolderByMenu ? [selectedFolderByMenu] : []}
                    dataType={"folder"}
                />
            )}
            {dialog === "duplicate" && (
                <DialogAssetDuplicate folder={selectedFolderByMenu} onClickClose={onHandleClickCloseDialog}
                                      isCms={isCms} isCustomMenu={isCustomMenu} />
            )}
            {dialog === "createCollection" && (
                <DialogCreateCollection
                    onClose={onHandleClickCloseDialog}
                    open={dialog === "createCollection"}
                    assetFolder={selectedFolderByMenu}
                />
            )}
            {dialog === "shareLink" &&
                ReactDOM.createPortal(
                    <LoadableDownloadLinkModal
                        domain={domain.toUpperCase()}
                        selectedFolder={selectedFolderByMenu}
                        onClickClose={onHandleClickCloseDialog}
                        onSubmit={() => {
                            onHandleClickCloseDialog();
                            // afterMutationWorks();
                        }}
                    />,
                    document.body,
                )}
        </>
    );
};

export default React.memo(FolderTreeView);
